import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Button } from "../../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { cn } from "../../utils/common/cn";
import { GRADE_COLORS } from "../../pages/AthenaPricingEngine/lib/api/state-of-play";
import type { DealException } from "../../pages/AthenaPricingEngine/lib/api/exceptions";

const pct = (value: number) => `${value.toFixed(2)}%`;

function money(value: number) {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  return `$${Math.round(value / 1_000)}K`;
}

function GradeBadge({ grade }: { grade: DealException["grade"] }) {
  const color = GRADE_COLORS[grade];
  return (
    <span
      className="inline-flex items-center border px-1.5 py-0.5 text-[11px] font-semibold"
      style={{ borderColor: color, color }}
    >
      {grade}
    </span>
  );
}

const helper = createColumnHelper<DealException>();

export function ExceptionsLogTable({
  rows,
  total,
  page,
  pageCount,
  onPageChange,
  isFetching,
}: {
  /** Just the current page -- fetched server-side, see useDealExceptions. */
  rows: DealException[];
  /** Total deals below floor across every page. */
  total: number;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  isFetching?: boolean;
}) {
  // Sorts only the currently-loaded page -- the server already orders pages
  // by shortfall (worst first), which is what backs "Next"/"Previous"; this
  // is a convenience re-order of what's on screen, not a global sort.
  const [sorting, setSorting] = useState<SortingState>([{ id: "shortfall", desc: false }]);
  const [selected, setSelected] = useState<DealException | null>(null);

  const columns = useMemo<ColumnDef<DealException, any>[]>(
    () => [
      helper.accessor("dealId", {
        header: "Deal",
        cell: (info) => <span className="font-medium text-foreground">{info.getValue()}</span>,
      }),
      helper.display({
        id: "segment",
        header: "Segment",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <GradeBadge grade={row.original.grade} />
            <span className="text-foreground/90">
              {row.original.office} / {row.original.industry}
            </span>
          </div>
        ),
      }),
      helper.accessor("fundedYear", {
        header: "Funded Year",
        cell: (info) => info.getValue() ?? "—",
      }),
      helper.accessor("financed", { header: "Financed", cell: (info) => money(info.getValue()) }),
      helper.accessor("yield", { header: "Yield", cell: (info) => pct(info.getValue()) }),
      helper.accessor("requiredYield", {
        header: "Required Yield",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("shortfall", {
        header: "Shortfall",
        cell: (info) => (
          <span className="font-semibold text-decline">{pct(info.getValue())}</span>
        ),
      }),
      helper.accessor("flaggedAt", {
        header: "Flagged",
        cell: (info) => <span className="text-muted-foreground">{info.getValue()}</span>,
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="overflow-x-auto border border-border bg-card">
        <Table className="min-w-[1000px] border-collapse">
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow
                key={group.id}
                className="border-border bg-secondary/50 hover:bg-secondary/50"
              >
                {group.headers.map((header, i) => {
                  const sortDir = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "h-auto px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                        i <= 2 || i === 7 ? "text-left" : "text-right",
                      )}
                    >
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground",
                          i > 2 && i < 7 ? "flex-row-reverse" : "",
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sortDir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : sortDir === "desc" ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                tabIndex={0}
                onClick={() => setSelected(row.original)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelected(row.original);
                  }
                }}
                className="cursor-pointer border-border/60 hover:bg-secondary/40 focus:bg-secondary/40 focus:outline-none"
              >
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "px-3 py-2.5 align-middle",
                      i <= 2 || i === 7 ? "text-left" : "text-right tabular-nums",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-3 flex items-center justify-between gap-4 text-xs text-muted-foreground">
        <span className="tabular-nums">
          Page {page} of {pageCount} · {total.toLocaleString("en-US")} deals below floor
          {isFetching ? " · loading…" : ""}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <Sheet open={selected != null} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 text-base">
                  {selected.dealId}
                  <GradeBadge grade={selected.grade} />
                </SheetTitle>
                <p className="text-xs text-muted-foreground">
                  {selected.office} / {selected.industry} · {money(selected.financed)} financed ·
                  flagged {selected.flaggedAt}
                </p>
              </SheetHeader>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="border border-border bg-card p-4">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Yield
                  </p>
                  <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">
                    {pct(selected.yield)}
                  </p>
                </div>
                <div className="border border-border bg-card p-4">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Required Yield
                  </p>
                  <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">
                    {pct(selected.requiredYield)}
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm text-foreground">
                Net RAV on this deal lands{" "}
                <span className="font-semibold text-decline">
                  {pct(selected.shortfall)}
                </span>{" "}
                against its approved target net margin.
              </p>

              <div className="mt-6 border border-border bg-card">
                <p className="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  What Makes Up Required Yield
                </p>
                <dl className="divide-y divide-border/60 text-sm">
                  {[
                    ["Funds Transfer Price", selected.ftp],
                    ["Expected Loss", selected.expectedLoss],
                    ["OpEx", selected.opex],
                    ["External Commission", selected.externalCommission],
                    ["Gross Margin", selected.grossMargin],
                  ].map(([label, value]) => (
                    <div key={label as string} className="flex justify-between px-4 py-2.5">
                      <dt className="text-muted-foreground">{label as string}</dt>
                      <dd className="tabular-nums text-foreground">{pct(value as number)}</dd>
                    </div>
                  ))}
                  <div className="flex justify-between bg-secondary/40 px-4 py-2.5 font-semibold">
                    <dt className="text-foreground">Required Yield</dt>
                    <dd className="tabular-nums text-foreground">
                      {pct(selected.requiredYield)}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="mt-6 border border-border bg-card">
                <p className="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Commission and RAV
                </p>
                <dl className="divide-y divide-border/60 text-sm">
                  {[
                    ["Gross RAV", pct(selected.grossRav)],
                    ["External Commission", pct(selected.externalCommission)],
                    [
                      "Internal Commission",
                      `${pct(selected.internalCommission)} · ${money(selected.internalCommissionDollars)} · ${selected.internalCommissionRate.toFixed(1)}% of gross margin`,
                    ],
                    ["Target Net Margin", pct(selected.targetNetMargin)],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4 px-4 py-2.5">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="tabular-nums text-foreground">{value}</dd>
                    </div>
                  ))}
                  <div className="flex justify-between bg-secondary/40 px-4 py-2.5 font-semibold">
                    <dt className="text-foreground">Net RAV</dt>
                    <dd
                      className={
                        selected.netRav < selected.targetNetMargin
                          ? "tabular-nums text-decline"
                          : "tabular-nums text-foreground"
                      }
                    >
                      {pct(selected.netRav)}
                    </dd>
                  </div>
                </dl>
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                The shortfall is measured on net RAV against the approved target net margin — after
                internal commission, not before it.
              </p>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
